import { useState } from "react";
import { api, useAction, useApi } from "../../api.ts";
import {
  Badge, Button, Card, Dialog, EmptyState, ErrorText, Field, Icon, IconButton, Input, Loading, LoadError, Mono, PageHeader, Select,
  Switch, Table, TBody, Td, Textarea, Th, THead, Tr, useToast,
} from "../../ui/index.tsx";
import { FormActions } from "./_ops.tsx";
import type { DetectionStatus, Game, GameCategory, GameStatus, Injection, PricingTier, WaitlistSummary } from "../../../shared/types.ts";

const STATUS_TONE = { operational: "ok", development: "info", maintenance: "warn", offline: "neutral", discontinued: "neutral" } as const satisfies Record<GameStatus, string>;
const DETECTION_TONE = { undetected: "ok", detected: "bad", updating: "warn", unknown: "neutral" } as const satisfies Record<DetectionStatus, string>;
const STATUSES = Object.keys(STATUS_TONE) as GameStatus[];
const DETECTIONS = Object.keys(DETECTION_TONE) as DetectionStatus[];
const CATEGORIES: GameCategory[] = ["survival", "fps", "sandbox", "other"];
const INJECTIONS: { value: Injection; label: string }[] = [
  { value: "internal", label: "Internal" },
  { value: "external", label: "External" },
  { value: "both", label: "Internal + External" },
];
const INJECTION_LABEL = Object.fromEntries(INJECTIONS.map((i) => [i.value, i.label])) as Record<Injection, string>;
const SUGGESTED_ANTI_CHEATS = ["BattlEye", "VAC", "EAC"];

export default function Games() {
  const games = useApi<Game[]>("/admin/games");
  const toast = useToast();
  const [editing, setEditing] = useState<Game | "new" | null>(null);

  const toggle = async (g: Game, active: boolean) => {
    try {
      const updated = await api.patch<Game>(`/admin/games/${g.id}`, { active });
      games.setData((prev) => (prev ?? []).map((x) => (x.id === g.id ? updated : x)));
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const sorted = [...(games.data ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));

  return (
    <>
      <PageHeader
        title="Games"
        description="Catalogue, status and pricing shown on the site and in the loader."
        actions={<Button variant="primary" icon="plus" onClick={() => setEditing("new")}>New game</Button>}
      />
      <Card padded={false}>
        {!games.data ? (
          games.loading ? <Loading /> : <LoadError error={games.error} retry={() => void games.reload()} />
        ) : sorted.length === 0 ? (
          <EmptyState icon="gamepad" title="No games yet" action={<Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>New game</Button>} />
        ) : (
          <Table>
            <THead><tr><Th>Game</Th><Th>ID</Th><Th>Status</Th><Th>Detection</Th><Th>Injection</Th><Th>Anti-cheat</Th><Th>Category</Th><Th>Order</Th><Th>Active</Th><Th /></tr></THead>
            <TBody>
              {sorted.map((g) => (
                <Tr key={g.id} className={g.active ? undefined : "opacity-60"}>
                  <Td className="font-medium">{g.name}</Td>
                  <Td><Mono>{g.id}</Mono></Td>
                  <Td><Badge tone={STATUS_TONE[g.status]} dot>{g.status}</Badge></Td>
                  <Td><Badge tone={DETECTION_TONE[g.detection_status]} dot>{g.detection_status}</Badge></Td>
                  <Td className="whitespace-nowrap text-muted">{INJECTION_LABEL[g.injection] ?? g.injection}</Td>
                  <Td>{g.anti_cheats.length > 0 ? <div className="flex flex-wrap gap-1">{g.anti_cheats.map((a) => <Badge key={a}>{a}</Badge>)}</div> : <span className="text-faint">None</span>}</Td>
                  <Td className="capitalize text-muted">{g.category}</Td>
                  <Td className="tabular-nums text-muted">{g.sort_order}</Td>
                  <Td><Switch checked={g.active} onChange={(v) => void toggle(g, v)} /></Td>
                  <Td className="text-right"><IconButton icon="edit" label="Edit" onClick={() => setEditing(g)} /></Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <WaitlistCard />

      <Dialog open={editing !== null} onClose={() => setEditing(null)} wide title={editing === "new" ? "New game" : `Edit ${editing?.name ?? ""}`}>
        {editing !== null && (
          <GameForm
            game={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void games.reload(); }}
          />
        )}
      </Dialog>
    </>
  );
}

function GameForm({ game, onClose, onSaved }: { game: Game | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [id, setId] = useState(game?.id ?? "");
  const [name, setName] = useState(game?.name ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [imageUrl, setImageUrl] = useState(game?.image_url ?? "");
  const [status, setStatus] = useState<GameStatus>(game?.status ?? "development");
  const [detection, setDetection] = useState<DetectionStatus>(game?.detection_status ?? "unknown");
  const [category, setCategory] = useState<GameCategory>(game?.category ?? "other");
  const [sortOrder, setSortOrder] = useState(String(game?.sort_order ?? 0));
  const [pricing, setPricing] = useState(game?.pricing ?? "");
  const [features, setFeatures] = useState<string[]>(game?.features ?? []);
  const [injection, setInjection] = useState<Injection>(game?.injection ?? "internal");
  const [antiCheats, setAntiCheats] = useState<string[]>(game?.anti_cheats ?? []);
  const [tiers, setTiers] = useState<PricingTier[]>(game?.pricing_tiers ?? []);
  const [active, setActive] = useState(game?.active ?? true);

  const idOk = /^[a-z0-9-]{2,32}$/.test(id);
  const save = useAction(async () => {
    const body = {
      name: name.trim(),
      description: description.trim(),
      image_url: imageUrl.trim(),
      status,
      detection_status: detection,
      category,
      sort_order: Number(sortOrder) || 0,
      pricing: pricing.trim(),
      features,
      injection,
      anti_cheats: antiCheats,
      pricing_tiers: tiers.map((t) => ({ dur: t.dur.trim(), price: t.price.trim() })).filter((t) => t.dur || t.price),
    };
    if (game) await api.patch<Game>(`/admin/games/${game.id}`, { ...body, active });
    else await api.post<Game>("/admin/games", { id, ...body });
    toast(game ? "Game saved" : "Game created", "success");
    onSaved();
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save.run(); }} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} required /></Field>
        <Field label="ID" hint={game ? "Can't be changed" : "a-z, 0-9 and hyphens, 2-32 characters"} error={!game && id && !idOk ? "Lowercase letters, digits and hyphens only (2-32)" : null}>
          <Input value={id} onChange={(e) => setId(e.target.value.toLowerCase())} className="font-mono" disabled={!!game} required />
        </Field>
      </div>
      <Field label="Description"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} /></Field>
      <Field label="Image URL"><Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://" /></Field>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as GameStatus)}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select>
        </Field>
        <Field label="Detection">
          <Select value={detection} onChange={(e) => setDetection(e.target.value as DetectionStatus)}>{DETECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}</Select>
        </Field>
        <Field label="Category">
          <Select value={category} onChange={(e) => setCategory(e.target.value as GameCategory)}>{CATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}</Select>
        </Field>
        <Field label="Sort order"><Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field label="Injection" hint="Shown on the site as a compatibility badge">
          <Select value={injection} onChange={(e) => setInjection(e.target.value as Injection)}>{INJECTIONS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}</Select>
        </Field>
      </div>
      <ChipEditor label="Anti-cheats" placeholder="Add an anti-cheat, press Enter" max={10} maxLength={40} suggestions={SUGGESTED_ANTI_CHEATS} value={antiCheats} onChange={setAntiCheats} />
      <FeaturesEditor value={features} onChange={setFeatures} />
      <TiersEditor value={tiers} onChange={setTiers} />
      <Field label="Pricing summary" hint="Free text shown when there are no tiers, e.g. &quot;From £9.99&quot;">
        <Input value={pricing} onChange={(e) => setPricing(e.target.value)} />
      </Field>
      {game && <Switch checked={active} onChange={setActive} label="Active (listed on the site)" />}
      <ErrorText>{save.error}</ErrorText>
      <FormActions onCancel={onClose} submitLabel={game ? "Save changes" : "Create game"} loading={save.pending} disabled={!name.trim() || (!game && !idOk)} />
    </form>
  );
}

function FeaturesEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  return <ChipEditor label="Features" placeholder="Add a feature, press Enter" value={value} onChange={onChange} />;
}

/** Editable list of short strings, with optional one-click suggestions. */
function ChipEditor({ label, placeholder, value, onChange, suggestions = [], max, maxLength }: {
  label: string; placeholder: string; value: string[]; onChange: (v: string[]) => void; suggestions?: string[]; max?: number; maxLength?: number;
}) {
  const [draft, setDraft] = useState("");
  const full = max !== undefined && value.length >= max;
  const add = (raw = draft) => {
    const f = raw.trim();
    if (f && !full && !value.some((x) => x.toLowerCase() === f.toLowerCase())) onChange([...value, f]);
    setDraft("");
  };
  const quick = suggestions.filter((s) => !value.some((x) => x.toLowerCase() === s.toLowerCase()));
  return (
    <div className="space-y-2">
      <span className="text-xs font-medium text-muted">{label}</span>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 rounded-md border border-line bg-raised py-0.5 pl-2 pr-1 text-xs">
              {f}
              <button type="button" aria-label={`Remove ${f}`} onClick={() => onChange(value.filter((x) => x !== f))} className="rounded p-0.5 text-faint hover:bg-hover hover:text-fg">
                <Icon name="x" className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={draft} maxLength={maxLength} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} disabled={full}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button onClick={() => add()} disabled={!draft.trim() || full} icon="plus">Add</Button>
      </div>
      {quick.length > 0 && !full && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-faint">
          Quick add:
          {quick.map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="rounded-md border border-dashed border-line px-2 py-0.5 text-muted hover:border-line-strong hover:text-fg">+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function TiersEditor({ value, onChange }: { value: PricingTier[]; onChange: (v: PricingTier[]) => void }) {
  const update = (i: number, patch: Partial<PricingTier>) => onChange(value.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted">Pricing tiers</span>
        <Button size="sm" variant="ghost" icon="plus" onClick={() => onChange([...value, { dur: "", price: "" }])}>Add tier</Button>
      </div>
      {value.length === 0 ? (
        <p className="text-xs text-faint">No tiers.</p>
      ) : value.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={t.dur} onChange={(e) => update(i, { dur: e.target.value })} placeholder="Duration, e.g. 30 days" aria-label="Duration" />
          <Input value={t.price} onChange={(e) => update(i, { price: e.target.value })} placeholder="Price, e.g. £9.99" aria-label="Price" />
          <IconButton icon="trash" label="Remove tier" onClick={() => onChange(value.filter((_, j) => j !== i))} />
        </div>
      ))}
    </div>
  );
}

function WaitlistCard() {
  const waitlist = useApi<WaitlistSummary[]>("/admin/waitlist");
  const rows = waitlist.data ?? [];
  return (
    <Card className="mt-6" title="Waitlists" description="Emails go out automatically when a game is set to Operational (requires email to be configured).">
      {!waitlist.data ? (
        waitlist.loading ? <Loading /> : <LoadError error={waitlist.error} retry={() => void waitlist.reload()} />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted">Nobody is waiting on a game yet.</p>
      ) : (
        <Table>
          <THead><tr><Th>Game</Th><Th>Waiting</Th><Th>Notified</Th></tr></THead>
          <TBody>
            {rows.map((r) => (
              <Tr key={r.game_id}>
                <Td className="font-medium">{r.game_name} <Mono>{r.game_id}</Mono></Td>
                <Td className="tabular-nums">{r.waiting}</Td>
                <Td className="tabular-nums text-muted">{r.notified}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
